import { Suspense, useEffect, useMemo, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Avatar, CopyButton, Drawer, ErrorState, EyeIcon, Field, LoadingState, Modal, MultiSelect, Pill, PlusIcon, SaveState, Tabs, useConfirm, useToast, type SaveStatus,
} from "@signet/ui";
import { ago, api, describeDropped, MatrixEditor, startViewAs, useMe, type Matrix, type ScopedMatrix } from "@signet/web-kit";
import { webModules } from "../../../../modules/registry.web.ts";

/* ──────────────────────────────────────────────────────────────────────────
   People: accounts, their roles, and what each person can do.

   Every account here is the same account the reporting site and the hub sign
   in with. Grants set here are clamped to what the person
   saving holds, and the screen says what was dropped rather than quietly
   saving less than was ticked.
   ────────────────────────────────────────────────────────────────────────── */

type Person = { id: string; username: string; name: string | null; isOwner: boolean; disabled: boolean; createdAt: string; lastSeen: string | null; roles: string[] };
type Role = { id: string; name: string };

/**
 * Staff, then a tab for each module that keeps its own kind of account
 * (Creators: every creator and their login, Jay 2026-10-13). Each module's
 * tab gets the sections other modules add for the accounts it binds.
 */
export function PeoplePage() {
  const { can } = useMe();
  const tabs = useMemo(
    () =>
      webModules
        .flatMap((mod) => (mod.peopleTabs ?? []).map((tab) => ({ ...tab, owner: mod.id })))
        .filter((tab) => can(tab.needs))
        .sort((a, b) => a.order - b.order),
    [can],
  );
  const [tab, setTab] = useState<string>(() => {
    try { return sessionStorage.getItem("signet.people.tab") ?? "staff"; } catch { return "staff"; }
  });
  const choose = (next: string) => {
    setTab(next);
    try { sessionStorage.setItem("signet.people.tab", next); } catch { /* private window */ }
  };
  const current = tabs.find((t) => t.id === tab) ?? null;
  const sections = useMemo(
    () => (current ? webModules.flatMap((mod) => (mod.portal?.for === current.owner ? mod.portal.staffSections ?? [] : [])).sort((a, b) => a.order - b.order) : []),
    [current],
  );
  const switcher = tabs.length ? (
    <div className="signet-people-tabs">
      <Tabs label="Kind of account" value={current ? current.id : "staff"} onChange={choose} options={[{ value: "staff", label: "Staff" }, ...tabs.map((t) => ({ value: t.id, label: t.label }))]} />
    </div>
  ) : null;
  if (current) {
    const Tab = current.component;
    return (
      <div className="page">
        <div className="page-head">
          <div>
            <h1>People</h1>
            <p>One account for every OGA tool. What each person can do in Signet is set here, per module.</p>
          </div>
        </div>
        {switcher}
        <Suspense fallback={<LoadingState />}><Tab sections={sections} /></Suspense>
      </div>
    );
  }
  return <StaffPeople switcher={switcher} />;
}

function StaffPeople({ switcher }: { switcher: React.ReactNode }) {
  const { can } = useMe();
  const [creating, setCreating] = useState(false);
  const [openId, setOpenId] = useState<string | null>(null);
  const people = useQuery({ queryKey: ["people"], queryFn: () => api<{ people: Person[] }>("/api/admin/people") });
  const roles = useQuery({ queryKey: ["roles"], queryFn: () => api<{ roles: Role[] }>("/api/admin/roles"), enabled: can("signet.roles:view") });
  const roleName = useMemo(() => new Map((roles.data?.roles ?? []).map((r) => [r.id, r.name])), [roles.data]);
  const open = people.data?.people.find((p) => p.id === openId) ?? null;

  return (
    <div className="page">
      <div className="page-head">
        <div>
          <h1>People</h1>
          <p>One account for every OGA tool. What each person can do in Signet is set here, per module.</p>
        </div>
        <div className="page-head-actions">
          {can("signet.people:create") ? <button type="button" className="button brass" onClick={() => setCreating(true)}><PlusIcon size={14} />Add a person</button> : null}
        </div>
      </div>
      {switcher}

      <section className="panel">
        {people.isLoading ? <LoadingState /> : people.error ? <ErrorState body={(people.error as Error).message} onRetry={() => people.refetch()} /> : (
          <div className="table" role="table">
            <div className="table-row head people-cols" role="row"><span>Person</span><span>Roles</span><span>Last seen</span><span>Status</span></div>
            {people.data!.people.map((p) => (
              // A real button on the name for keyboards and screen readers; the
              // whole row also opens it, for the mouse.
              <div key={p.id} className="table-row people-cols clickable" role="row" onClick={() => setOpenId(p.id)}>
                <span className="row" role="cell">
                  <Avatar name={p.name ?? p.username} size="small" />
                  <button type="button" className="as-button row-link" onClick={(e) => { e.stopPropagation(); setOpenId(p.id); }}>
                    <strong>{p.name ?? p.username}</strong>
                    {p.name ? <small className="faint"> {p.username}</small> : null}
                  </button>
                </span>
                <span className="muted truncate" role="cell">{p.isOwner ? "Owner" : p.roles.map((r) => roleName.get(r) ?? r).join(", ") || "No role"}</span>
                <span className="faint" role="cell">{p.lastSeen ? ago(p.lastSeen) : "Never"}</span>
                <span role="cell">{p.disabled ? <Pill tone="idle">Disabled</Pill> : <Pill tone="good" dot>Active</Pill>}</span>
              </div>
            ))}
          </div>
        )}
      </section>

      <CreatePerson open={creating} onClose={() => setCreating(false)} roles={roles.data?.roles ?? []} />
      {open ? <PersonDrawer person={open} roles={roles.data?.roles ?? []} onClose={() => setOpenId(null)} /> : null}
    </div>
  );
}

function CreatePerson({ open, onClose, roles }: { open: boolean; onClose(): void; roles: Role[] }) {
  const queryClient = useQueryClient();
  const [username, setUsername] = useState("");
  const [name, setName] = useState("");
  const [chosen, setChosen] = useState<string[]>([]);
  const [result, setResult] = useState<{ username: string; password: string } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const create = useMutation({
    mutationFn: () => api<{ id: string; password: string }>("/api/admin/people", { method: "POST", body: { username, name: name.trim() || undefined, roles: chosen } }),
    onSuccess: (data) => {
      setResult({ username, password: data.password });
      queryClient.invalidateQueries({ queryKey: ["people"] });
    },
    onError: (err) => setError((err as Error).message),
  });
  const close = () => {
    setUsername("");
    setChosen([]);
    setResult(null);
    setError(null);
    onClose();
  };

  return (
    <Modal open={open} onClose={close} title={result ? `${result.username} is ready` : "Add a person"} footer={
      result ? <button type="button" className="button" onClick={close}>Done</button> : (
        <>
          <button type="button" className="text-button" onClick={close}>Cancel</button>
          <button type="button" className="button brass" disabled={!username || create.isPending} onClick={() => create.mutate()}>Add person</button>
        </>
      )
    }>
      {result ? (
        <div className="col">
          <p>Send them this password. It's shown once. They'll set up an authenticator app the first time they sign in.</p>
          <div className="secret-box"><code>{result.password}</code><CopyButton value={result.password} label="Copy password" /></div>
        </div>
      ) : (
        <div className="col">
          <Field label="Name" hint="What everyone calls them, like Jay. Shown instead of the username across Signet. Optional.">
            <input autoFocus value={name} onChange={(e) => setName(e.target.value)} />
          </Field>
          <Field label="Username" hint="Lowercase letters, digits, dots and dashes. It's what they type to sign in.">
            <input value={username} onChange={(e) => setUsername(e.target.value.toLowerCase())} autoCapitalize="none" spellCheck={false} />
          </Field>
          {roles.length ? (
            <Field label="Roles">
              <MultiSelect label="Roles" values={chosen} onChange={setChosen} options={roles.map((r) => ({ value: r.id, label: r.name }))} placeholder="No role" />
            </Field>
          ) : null}
          {error ? <p className="field-error">{error}</p> : null}
        </div>
      )}
    </Modal>
  );
}

function PersonDrawer({ person, roles, onClose }: { person: Person; roles: Role[]; onClose(): void }) {
  const who = person.name ?? person.username;
  const { me, can } = useMe();
  const toast = useToast();
  const { confirm, element } = useConfirm();
  const queryClient = useQueryClient();
  const grants = useQuery({
    queryKey: ["people", person.id, "grants"],
    queryFn: () => api<{ direct: Matrix; inherited: Matrix; scopedDirect: ScopedMatrix; scopedInherited: ScopedMatrix }>(`/api/admin/people/${person.id}/grants`),
  });
  const [matrix, setMatrix] = useState<Matrix>({});
  const [scoped, setScoped] = useState<ScopedMatrix>({});
  const [chosenRoles, setChosenRoles] = useState<string[]>(person.roles);
  const [status, setStatus] = useState<SaveStatus>("idle");
  const [password, setPassword] = useState<string | null>(null);
  const passwordBox = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (password) passwordBox.current?.scrollIntoView({ block: "nearest", behavior: "smooth" });
  }, [password]);

  useEffect(() => {
    if (grants.data) {
      setMatrix(grants.data.direct);
      setScoped(grants.data.scopedDirect);
    }
  }, [grants.data]);

  const editable = can("signet.people:edit") && !person.isOwner && person.id !== me?.user.id;
  // One owner can still get another back in. With the reset hidden on every owner, Jay forgot his
  // password and neither he nor Ben had a button for it (2026-10-22). The API already allowed it.
  const canReset = editable || Boolean(me?.user.isOwner && person.isOwner && person.id !== me?.user.id);
  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: ["people"] });
    queryClient.invalidateQueries({ queryKey: ["me"] });
    queryClient.invalidateQueries({ queryKey: ["signet", "names"] });
  };

  async function save() {
    setStatus("saving");
    try {
      const rolesResult = await api<{ droppedRoles: string[] }>(`/api/admin/people/${person.id}`, { method: "PATCH", body: { roles: chosenRoles } });
      const result = await api<{ granted: Matrix; dropped: Matrix; droppedScoped: ScopedMatrix }>(`/api/admin/people/${person.id}/grants`, { method: "PUT", body: { matrix, scoped } });
      setStatus("saved");
      refresh();
      const dropped = [describeDropped(result.dropped, me?.resources ?? []), Object.keys(result.droppedScoped ?? {}).length ? "some per-record access" : ""].filter(Boolean).join("; ");
      if (dropped || rolesResult.droppedRoles.length) {
        toast.push({ tone: "info", title: "Saved, with some left out", body: `You can only give what you have yourself. Left out: ${[dropped, rolesResult.droppedRoles.length ? `${rolesResult.droppedRoles.length} role(s)` : ""].filter(Boolean).join("; ")}` });
      }
    } catch (error) {
      setStatus("failed");
      toast.push({ tone: "bad", title: "Couldn't save", body: (error as Error).message });
    }
  }

  async function action(label: string, run: () => Promise<void>, options: { body: string; danger?: boolean }) {
    if (!(await confirm({ title: label, body: options.body, confirmLabel: label, danger: options.danger }))) return;
    try {
      await run();
      refresh();
    } catch (error) {
      toast.push({ tone: "bad", title: `Couldn't ${label.toLowerCase()}`, body: (error as Error).message });
    }
  }

  return (
    <Drawer
      open
      narrow
      onClose={onClose}
      title={who}
      subtitle={(person.name ? `${person.username}. ` : "") + (person.isOwner ? "Owner: can do everything" : person.disabled ? "Disabled" : `Last seen ${person.lastSeen ? ago(person.lastSeen) : "never"}`)}
      footer={editable ? (
        <>
          <SaveState status={status} />
          <div className="spacer" />
          <button type="button" className="button brass" onClick={save} disabled={status === "saving"}>Save access</button>
        </>
      ) : null}
    >
      {element}
      <div className="signet-drawer">
      {can("signet.people:edit") ? <NameEditor person={person} onSaved={refresh} /> : null}
      {can("signet.people:edit") ? <UsernameEditor person={person} onSaved={refresh} /> : null}

      {me?.user.isOwner && !person.disabled && person.id !== me.user.id ? (
        <button type="button" className="secondary-button signet-view-as-button" onClick={() => startViewAs({ kind: "user", id: person.id, label: who })}>
          <EyeIcon size={15} />View Signet as {who}
        </button>
      ) : null}

      {person.isOwner ? (
        <p className="muted">Owners have every permission in every module. That can't be changed here.</p>
      ) : (
        <>
          {roles.length ? (
            <Field label="Roles" hint="Roles are shared sets of permissions. Change a role and everyone in it changes.">
              <MultiSelect label="Roles" values={chosenRoles} onChange={(v) => { setChosenRoles(v); setStatus("dirty"); }} options={roles.map((r) => ({ value: r.id, label: r.name }))} placeholder="No role" disabled={!editable} />
            </Field>
          ) : null}
          <div className="signet-gap" />
          <h3 className="drawer-subhead">Extra permissions for {who}</h3>
          <p className="faint small-print">Dimmed ticks come from their roles.</p>
          {grants.isLoading ? <LoadingState /> : (
            <MatrixEditor
              resources={me?.resources ?? []}
              value={matrix}
              inherited={grants.data?.inherited}
              grantable={me?.user.isOwner ? "all" : me?.matrix ?? {}}
              readOnly={!editable}
              onChange={(next) => { setMatrix(next); setStatus("dirty"); }}
              scoped={scoped}
              inheritedScoped={grants.data?.scopedInherited}
              grantableScoped={me?.scoped}
              onScopedChange={(next) => { setScoped(next); setStatus("dirty"); }}
            />
          )}
        </>
      )}

      {canReset ? (
        <div className="danger-zone">
          <button type="button" className="secondary-button tiny" onClick={() => action("Reset password", async () => {
            setPassword((await api<{ password: string }>(`/api/admin/people/${person.id}/password`, { method: "POST", body: {} })).password);
          }, { body: `${who} is signed out everywhere and needs the new password to get back in.` })}>Reset password</button>
          {me?.user.isOwner ? (
            <button type="button" className="secondary-button tiny" onClick={() => action("Reset authenticator", async () => {
              await api(`/api/admin/people/${person.id}/reset-factors`, { method: "POST", body: {} });
              toast.push({ tone: "good", title: "Authenticator reset", body: `${who} sets up a new one at their next sign-in.` });
            }, { body: "Clears their authenticator app, recovery codes and remembered devices. Their password stays the same." })}>Reset authenticator</button>
          ) : null}
          {!editable ? null : person.disabled ? (
            <button type="button" className="secondary-button tiny" onClick={() => action("Enable account", () => api(`/api/admin/people/${person.id}`, { method: "PATCH", body: { disabled: false } }).then(() => {}), { body: `${who} can sign in again.` })}>Enable account</button>
          ) : can("signet.people:delete") ? (
            <button type="button" className="secondary-button tiny danger" onClick={() => action("Disable account", () => api(`/api/admin/people/${person.id}`, { method: "PATCH", body: { disabled: true } }).then(() => {}), { body: `${who} is signed out of every OGA tool and can't sign back in. You can enable them again later.`, danger: true })}>Disable account</button>
          ) : null}
          {editable && can("signet.people:delete") ? (
            <button type="button" className="secondary-button tiny danger" onClick={() => action("Delete account", async () => {
              const result = await api<{ outcome: "deleted" | "removed" }>(`/api/admin/people/${person.id}`, { method: "DELETE" });
              toast.push({
                tone: "good",
                title: `${who} deleted`,
                body: result.outcome === "deleted"
                  ? "Their account is gone."
                  : "They're gone from Signet and can't sign in. Their name stays on things they did in the older tools, so that history still makes sense.",
              });
              onClose();
            }, {
              body: `${who} is signed out, can't sign in again, and disappears from People for good. This can't be undone. To keep the option of bringing them back, disable instead.`,
              danger: true,
            })}>Delete account</button>
          ) : null}
        </div>
      ) : null}
      {password ? (
        // Under the button that made it. At the top of this long drawer it was out of
        // sight, and Jay read the reset as having done nothing.
        <div ref={passwordBox} className="panel flat callout">
          <p>New password for {who} ({person.username}). It's shown once.</p>
          <div className="secret-box"><code>{password}</code><CopyButton value={password} label="Copy password" /></div>
        </div>
      ) : null}
      </div>
    </Drawer>
  );
}

/** The name people call them, shown instead of the username across Signet (Jay, 2026-10-08). */
function NameEditor({ person, onSaved }: { person: Person; onSaved: () => void }) {
  const [name, setName] = useState(person.name ?? "");
  const toast = useToast();
  const save = useMutation({
    mutationFn: () => api(`/api/admin/people/${person.id}`, { method: "PATCH", body: { name: name.trim() || null } }),
    onSuccess: () => {
      onSaved();
      toast.push({ tone: "good", title: name.trim() ? `Name saved: ${name.trim()}` : "Name cleared", body: name.trim() ? undefined : `Signet shows ${person.username} instead.` });
    },
    onError: (error) => toast.push({ tone: "bad", title: "Couldn't save the name", body: (error as Error).message }),
  });
  return (
    <Field label="Name" hint={`Shown instead of the username (${person.username}) across Signet.`}>
      <div className="signet-name-edit">
        <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Like Jay" maxLength={60} />
        <button type="button" className="secondary-button" disabled={save.isPending || name.trim() === (person.name ?? "")} onClick={() => save.mutate()}>
          Save name
        </button>
      </div>
    </Field>
  );
}

/** What they type to sign in (Jay, 2026-10-14). Old history that recorded the old one still shows them. */
function UsernameEditor({ person, onSaved }: { person: Person; onSaved: () => void }) {
  const [username, setUsername] = useState(person.username);
  const toast = useToast();
  const next = username.trim();
  const save = useMutation({
    mutationFn: () => api(`/api/admin/people/${person.id}`, { method: "PATCH", body: { username: next } }),
    onSuccess: () => {
      onSaved();
      toast.push({ tone: "good", title: `Username changed to ${next}`, body: "They sign in with it from now on. They stay signed in where they are." });
    },
    onError: (error) => toast.push({ tone: "bad", title: "Couldn't change the username", body: (error as Error).message }),
  });
  return (
    <Field label="Username" hint="What they type to sign in. Always lowercase.">
      <div className="signet-name-edit">
        <input value={username} onChange={(e) => setUsername(e.target.value.toLowerCase())} maxLength={40} autoCapitalize="none" spellCheck={false} autoComplete="off" />
        <button type="button" className="secondary-button" disabled={save.isPending || next.length < 2 || next === person.username} onClick={() => save.mutate()}>
          Save username
        </button>
      </div>
    </Field>
  );
}
